import PropTypes from 'prop-types';

import { needsDescription } from '../../core/utils/images';

/**
 * Caption text sits over an arbitrary photo, so every state uses full-opacity
 * white plus a shadow. The translucent `white-72` / `white-88` shades that a
 * secondary state would normally use are unreadable here — the photo shows
 * through them.
 */
const CAPTION_CLASSES = 'text-white-100 [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]';

/**
 * One gallery tile. An image without a description is deliberately conspicuous —
 * an orange ring and badge — because an undescribed image cannot be chosen for a
 * generated email, so "incomplete" needs to read as incomplete.
 */
const ImageTile = ({ image, selected, describing, onSelect }) => {
  const missing = needsDescription(image);

  return (
    <button
      type="button"
      onClick={() => onSelect(image.imageId)}
      aria-pressed={selected}
      className={`
        group relative aspect-[4/3] overflow-hidden rounded-lg text-left
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
        ${selected ? 'ring-2 ring-primary-500' : ''}
        ${missing && !describing && !selected ? 'ring-1 ring-orange-500' : ''}
      `}
    >
      <img
        src={image.url}
        alt={image.description || image.filename}
        loading="lazy"
        className="size-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
      />

      {missing && !describing && (
        <span className="absolute left-2 top-2 rounded bg-orange-500 px-1.5 py-0.5 text-xs font-medium text-white-100">
          Needs description
        </span>
      )}

      {/* Scrim: taller than the text and opaque at the base, so a two-line
          description's top line still sits on a dark ground rather than in the
          faded-out part of the gradient. The text shadow is the backstop for a
          bright photo where even the scrim is not enough. */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-950/95 via-gray-950/75 to-transparent px-2 pb-2 pt-8">
        {describing ? (
          <p className={`animate-pulse text-sm ${CAPTION_CLASSES}`}>Writing description…</p>
        ) : missing ? (
          <p className={`text-sm italic ${CAPTION_CLASSES}`}>No description</p>
        ) : (
          <p className={`line-clamp-2 text-sm ${CAPTION_CLASSES}`}>{image.description}</p>
        )}
      </div>
    </button>
  );
};

ImageTile.propTypes = {
  image: PropTypes.shape({
    imageId: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    filename: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool,
  describing: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

ImageTile.defaultProps = {
  selected: false,
  describing: false,
};

export default ImageTile;
